import React, { useMemo, useState } from "react";
import { CustomTextField } from "../../components/TextFields";
import ImageUploader from "../../components/uploader/imageUploader";
import { Row } from "../../global/styledComponents";
import FileUploader from "../../components/uploader/fileUploader";
import { SelectInput } from "../../components/inputs/SelectInput";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import {
  ModalButtonContainer,
  NegativeModalButton,
  PositiveModalButton,
} from "../../components/Button";
import { useModal } from "../../components/modal";
import { SwitchButton } from "../../components/switch";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useGetUsersQuery } from "../../app/services/usres.api";
import {
  useAddCourseMutation,
  useGetCoursesQuery,
  useUpdateCourseMutation,
} from "../../app/services/courses.api";
import toastError from "../../components/notif";
import { apiErrorMessage } from "../../utils/apiError";
import { discountPercent } from "../../utils/discount";
import objToFromData from "../../utils/objToFromdata";
import { Level } from "../../types/level";
import { getUserName } from "../../types/user";
import LoadingButton from "../../components/Button/LoadingButton";
import { Course } from "../../types/course";
import TextContainer from "../../components/Texts/TextContainer";
import { useNavigate } from "react-router-dom";

function CreatedCourse({
  course,
  onClose,
}: {
  course: Partial<Course>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  return (
    <React.Fragment>
      <TextContainer color="divider" margin="0 0 1rem 0">
        دوره «{course.title}» ساخته شد. حالا جلسه‌ها و ویدیوهای آن را اضافه کنید
        (بعداً هم از دکمهٔ «جلسات» در فهرست دوره‌ها در دسترس است).
      </TextContainer>
      <ModalButtonContainer>
        <Button
          style={NegativeModalButton}
          sx={{
            color: "divider",
            bgcolor: "primary.main",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "divider",
          }}
          onClick={onClose}
        >
          بستن
        </Button>
        <Button
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          onClick={() => {
            onClose();
            navigate(`/courses/${course.slug}/snacks`, {
              state: { courseId: course._id, title: course.title },
            });
          }}
        >
          افزودن جلسه / ویدیو
        </Button>
      </ModalButtonContainer>
    </React.Fragment>
  );
}

const schema = yup.object().shape({
  title: yup.string().required("عنوان الزامی است."),
  slug: yup.string().required("اسلاگ الزامی است"),
  level: yup.string().required("سطح دوره الزامی است."),
  desc: yup.string().required("توضیحات الزامی است."),
  teacher: yup.string().required("استاد الزامی است."),
  price: yup.number().required("قیمت الزامی است."),
  audience: yup.string(),
  achievements: yup.string(),
  category: yup.string().trim().required("دسته بندی الزامی است."),
  discount: yup
    .number()
    .typeError("تخفیف باید عدد باشد.")
    .min(0, "تخفیف نمی‌تواند منفی باشد.")
    .max(100, "تخفیف درصد است و حداکثر ۱۰۰ است.")
    .default(0),
  books: yup.number(),
  lock: yup.boolean().required(),
  organPrivate: yup.boolean(),
  banner: yup.string().required("تصویر دوره الزامی است."),
  teaser: yup.string(),
});

export default function AddModal({ defaultValue }: { defaultValue?: Course }) {
  const {
    setValue,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValue
      ? {
          title: defaultValue?.title,
          slug: defaultValue?.slug,
          level: defaultValue?.level,
          desc: defaultValue?.desc,
          teacher: defaultValue?.teacher._id,
          price: defaultValue?.price,
          audience: defaultValue?.audience,
          achievements: defaultValue?.achievements,
          category: defaultValue?.category,
          discount: discountPercent(defaultValue?.discount),
          books: defaultValue?.books,
          lock: defaultValue?.lock,
          organPrivate: defaultValue?.organPrivate === true,
          banner: defaultValue?.banner,
          teaser: defaultValue?.teaser,
        }
      : { lock: false, organPrivate: false, level: Level.BEGINNER, discount: 0 },
  });
  const { setShow } = useModal();
  const [banner, setBanner] = useState<File | null>(null);
  const [teaser, setTeaser] = useState<File | null>(null);
  const { data: users } = useGetUsersQuery({ teacher: true });
  const [addCourse, addState] = useAddCourseMutation();
  const [updateCourse, updateState] = useUpdateCourseMutation();
  const isEdit = defaultValue ? true : false;
  const [created, setCreated] = useState<Partial<Course> | null>(null);
  // categories are free text on the course; offer the ones already in use
  const { data: allCourses } = useGetCoursesQuery({});
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    (allCourses?.courses?.ids || []).forEach((id) => {
      const c = allCourses?.courses?.entities[id]?.category;
      if (c && c.trim()) set.add(c.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fa"));
  }, [allCourses]);
  const levelOptions = [
    { label: "مبتدی", value: Level.BEGINNER },
    { label: "متوسط", value: Level.MEDUIM },
    { label: "پیشرفته", value: Level.ADVANCED },
  ];

  if (created)
    return <CreatedCourse course={created} onClose={() => setShow(false)} />;
  return (
    <React.Fragment>
      <Row>
        <CustomTextField
          required
          label="عنوان"
          placeholder="عنوان را وارد کنید."
          variant="outlined"
          onChange={(e) => setValue("title", e.target.value)}
          defaultValue={getValues("title")}
          error={!!errors.title}
          helperText={errors.title?.message}
        />
        <CustomTextField
          required
          label="اسلاگ"
          placeholder="اسلاگ را وارد نمایید"
          variant="outlined"
          onChange={(e) => setValue("slug", e.target.value)}
          defaultValue={getValues("slug")}
          error={!!errors.slug}
          helperText={errors.slug?.message}
        />
      </Row>
      <ImageUploader
        required
        title="تصویر"
        onChange={(e) => {
          setBanner(e);
          setValue("banner", e?.name ?? "");
        }}
      />
      {errors.banner && (
        <FormHelperText error sx={{ width: "100%", textAlign: "right", mt: -2, mb: 2 }}>
          {errors.banner.message}
        </FormHelperText>
      )}
      <Autocomplete
        freeSolo
        fullWidth
        options={categoryOptions}
        defaultValue={getValues("category") ?? ""}
        onChange={(_, v) => setValue("category", (v as string) ?? "")}
        onInputChange={(_, v) => setValue("category", v ?? "")}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label="دسته بندی"
            required
            placeholder="از فهرست انتخاب کنید یا دسته جدید بنویسید"
            variant="outlined"
            error={!!errors.category}
            helperText={errors.category?.message}
          />
        )}
      />

      <Row>
        <SelectInput
          label="سطح دوره"
          error={!!errors.level}
          helperText={errors.level?.message}
          onChange={(e) => setValue("level", e?.value)}
          options={levelOptions}
          defaultValue={levelOptions.find((o) => o.value === getValues("level"))}
        />
        <CustomTextField
          label=" قیمت"
          required
          placeholder=" قیمت را وارد کنید."
          variant="outlined"
          type="number"
          onChange={(e) => setValue("price", Number(e.target.value))}
          defaultValue={getValues("price")}
          error={!!errors.price}
          helperText={errors.price?.message}
        />
      </Row>
      <CustomTextField
        label=" تخفیف (درصد)"
        placeholder=" مثلاً 15 برای ۱۵٪ (۰ یعنی بدون تخفیف)"
        variant="outlined"
        type="number"
        onChange={(e) => setValue("discount", Number(e.target.value))}
        defaultValue={getValues("discount")}
        error={!!errors.discount}
        helperText={errors.discount?.message}
      />
      <Row>
        <SwitchButton
          label="محدودیت"
          defaultValue={getValues("lock") === true}
          onChange={(ch) => setValue("lock", ch)}
        />
      </Row>
      <Row>
        <div style={{ width: "100%", padding: "0 .5rem" }}>
          <FormControlLabel
            sx={{ color: "divider", marginRight: 0 }}
            control={
              <Checkbox
                defaultChecked={getValues("organPrivate") === true}
                onChange={(_, ch) => setValue("organPrivate", ch)}
                sx={{
                  color: "divider",
                  "&.Mui-checked": { color: "customColors.purple" },
                }}
              />
            }
            label="خصوصی سازمان"
          />
          <FormHelperText sx={{ color: "divider", textAlign: "right" }}>
            فقط اعضای سازمان‌هایی که این دوره را دارند آن را می‌بینند. بدون
            تیک، دوره عمومی می‌ماند و اتصال به سازمان یعنی اعضا بدون خرید
            دسترسی دارند.
          </FormHelperText>
        </div>
      </Row>
      <CustomTextField
        label=" توضیحات"
        placeholder=" توضیحات را وارد کنید."
        variant="outlined"
        onChange={(e) => setValue("desc", e.target.value)}
        defaultValue={getValues("desc")}
        error={!!errors.desc}
        helperText={errors.desc?.message}
        multiline
        minRows={2}
        maxRows={4}
      />
      <CustomTextField
        label=" مخاطبان"
        placeholder=" مخاطبان را وارد کنید."
        variant="outlined"
        onChange={(e) => setValue("audience", e.target.value)}
        defaultValue={getValues("audience")}
        error={!!errors.audience}
        helperText={errors.audience?.message}
        multiline
        minRows={2}
        maxRows={4}
      />
      <CustomTextField
        label=" دستاوردها"
        placeholder=" دستاوردها را وارد کنید."
        variant="outlined"
        onChange={(e) => setValue("achievements", e.target.value)}
        defaultValue={getValues("achievements")}
        error={!!errors.achievements}
        helperText={errors.achievements?.message}
        multiline
        minRows={2}
        maxRows={4}
      />
      <Row>
        <SelectInput
          label="استاد"
          error={!!errors.teacher}
          helperText={errors.teacher?.message}
          onChange={(e) => setValue("teacher", e?.value)}
          options={(users?.users?.ids || []).map((id) => ({
            label: getUserName(users?.users?.entities[id]),
            value: id,
          }))}
          defaultValue={
            defaultValue
              ? {
                  label: defaultValue?.teacher.userName,
                  value: defaultValue.teacher._id,
                }
              : undefined
          }
        />
        <CustomTextField
          label=" کتاب معادل"
          placeholder=" کتاب معادل را وارد کنید."
          variant="outlined"
          type="number"
          onChange={(e) => setValue("books", Number(e.target.value))}
          defaultValue={getValues("books")}
          error={!!errors.books}
          helperText={errors.books?.message}
        />
      </Row>
      <FormHelperText sx={{ color: "divider", textAlign: "right", width: "100%", mt: -1, mb: 2 }}>
        استاد در فهرست نیست؟ از منوی «کاربران» یک کاربر بسازید (یا کاربر موجود را
        ویرایش کنید) و «نوع کاربری» را «استاد» بگذارید.
      </FormHelperText>
      <FileUploader
        title="تیزر (اختیاری)"
        accept="video/*"
        helperText="فقط ویدیوی معرفی کوتاه. ویدیوی جلسات بعد از ساخت دوره، از منوی همان دوره ← «اسنک ها» اضافه می‌شود."
        onChange={(e) => setTeaser(e)}
      />
      <ModalButtonContainer>
        <Button
          style={NegativeModalButton}
          sx={{
            color: "divider",
            bgcolor: "primary.main",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "divider",
            ":hover": {
              bgcolor: "divider",
              color: "primary.main",
            },
          }}
          onClick={() => setShow(false)}
        >
          انصراف
        </Button>
        <LoadingButton
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          isLoading={addState.isLoading || updateState.isLoading}
          onClick={handleSubmit(
            (data) => {
              const {
                teaser: nteaser,
                banner: bn,
                lock,
                organPrivate,
                discount,
                ...params
              } = data;
              const formData = objToFromData(params);
              // backend stores a 0..1 fraction (@Max(1)); the form edits a percent
              formData.append("discount", String((Number(discount) || 0) / 100));
              // only "true" is sent: the backend lock field has no string->bool
              // transform, so "false" would be read as true
              if (lock) formData.append("lock", "true");
              // always sent so unticking on edit makes the course public again
              formData.append("organPrivate", organPrivate ? "true" : "false");
              if (banner) formData.append("banner", banner);
              if (teaser) formData.append("teaser", teaser);

              const onError = (err: any) => toastError(apiErrorMessage(err));
              if (isEdit) {
                return updateCourse({
                  data: formData,
                  courseId: defaultValue?._id as string,
                })
                  .unwrap()
                  .then(() => setShow(false))
                  .catch(onError);
              }

              addCourse(formData)
                .unwrap()
                .then((res) => {
                  if (res && res._id && res.slug) setCreated(res);
                  else setShow(false);
                })
                .catch(onError);
            },
            (errs) =>
              toastError(
                "فرم کامل نیست: " +
                  Object.values(errs)
                    .map((e: any) => e?.message)
                    .filter(Boolean)
                    .join("، ")
              )
          )}
        >
          تایید
        </LoadingButton>
      </ModalButtonContainer>
    </React.Fragment>
  );
}
